import { useState } from 'react';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import {
  INVITE_ARTWORK_CONTENT_TYPES,
  type InviteCampaign,
  type InviteDesign,
} from '@peles/shared';
import { SearchableListSelect } from '../../common';
import { useDialog } from '../../../context/DialogContext';
import {
  useCreateInviteDesign,
  useDeleteDesignPage,
  useInviteCampaigns,
  useInviteDesigns,
  useUploadDesignPage,
} from '../../../hooks/useInvites';
import { useTimelines } from '../../../hooks/useEventOps';
import { isPdfFile, pdfToImageFiles } from '../../../utils/pdfToImages';
import { DEFAULT_ENVELOPE_COLOR } from '../EnvelopeReveal';
import { StepFooter } from './StepFooter';
import { useStepDraft, withSettings } from './useStepDraft';
import { DEFAULT_INVITE_ACCENT, InvitePreview, type CampaignEventInfo } from './InvitePreview';
import type { CampaignStepProps } from './CampaignDetailsStep';

const DEFAULT_BACKGROUND = '#ffffff';

/** What the file picker offers: the images artwork may be, and a PDF to turn into them. */
const ARTWORK_ACCEPT = [...INVITE_ARTWORK_CONTENT_TYPES, 'application/pdf'].join(',');

/** A design with something to show: a page, or an older single cover. */
const hasArtwork = (design: InviteDesign) => (design.pages?.length ?? 0) > 0 || !!design.s3Key;

const isArtworkImage = (file: File) =>
  (INVITE_ARTWORK_CONTENT_TYPES as readonly string[]).includes(file.type);

interface DesignDraft {
  designId: string;
  timelineId: string;
  showTimeline: boolean;
  backgroundColor: string;
  accentColor: string;
  includeEventLogo: boolean;
  showEnvelope: boolean;
  envelopeSealLogo: boolean;
  envelopeColor: string;
  sealColor: string;
  envelopeMessage: string;
}

function designOf(campaign: InviteCampaign): DesignDraft {
  const settings = campaign.settings ?? {};
  const accent = settings.accentColor ?? DEFAULT_INVITE_ACCENT;
  return {
    designId: campaign.designId ?? '',
    timelineId: campaign.timelineId ?? '',
    showTimeline: settings.showTimeline === true,
    backgroundColor: settings.backgroundColor ?? DEFAULT_BACKGROUND,
    accentColor: accent,
    includeEventLogo: settings.includeEventLogo === true,
    showEnvelope: settings.showEnvelope !== false,
    envelopeSealLogo: settings.envelopeSealLogo === true,
    envelopeColor: settings.envelopeColor ?? DEFAULT_ENVELOPE_COLOR,
    sealColor: settings.sealColor ?? accent,
    envelopeMessage: settings.envelopeMessage ?? '',
  };
}

function ColourField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-600">
        {label}
      </label>
      <input
        id={id}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-16 rounded border border-gray-300"
      />
    </div>
  );
}

/** The hidden file input both upload controls share: an image, or a PDF. */
function ArtworkInput({ onFile, disabled }: { onFile: (file: File) => void; disabled: boolean }) {
  return (
    <input
      type="file"
      accept={ARTWORK_ACCEPT}
      className="sr-only"
      aria-label="Upload invitation artwork"
      disabled={disabled}
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onFile(file);
        e.target.value = '';
      }}
    />
  );
}

/** Beside the artwork picker: add another design or another page. */
function ArtworkUploadButton({
  label,
  busy,
  onFile,
}: {
  label: string;
  busy: boolean;
  onFile: (file: File) => void;
}) {
  return (
    <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-primary-500 sm:min-h-0">
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Upload className="h-4 w-4" aria-hidden />
      )}
      {busy ? 'Uploading…' : label}
      <ArtworkInput onFile={onFile} disabled={busy} />
    </label>
  );
}

/** Before there is any artwork: one large place to put it, or drop it. */
function ArtworkDropZone({ busy, onFile }: { busy: boolean; onFile: (file: File) => void }) {
  const [over, setOver] = useState(false);
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && !busy) onFile(file);
      }}
      className={`mt-1 flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center focus-within:ring-2 focus-within:ring-primary-500 ${
        over ? 'border-primary-400 bg-primary-50' : 'border-gray-300 bg-white hover:bg-gray-50'
      }`}
    >
      {busy ? (
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden />
      ) : (
        <ImagePlus className="h-6 w-6 text-gray-400" aria-hidden />
      )}
      <span className="text-sm font-medium text-gray-800">
        {busy ? 'Uploading…' : 'Upload the invitation artwork'}
      </span>
      <span className="text-xs text-gray-500">
        A picture (PNG, JPEG, WebP, GIF) or a PDF — tap to choose, or drop it here
      </span>
      <ArtworkInput onFile={onFile} disabled={busy} />
    </label>
  );
}

/**
 * Step 2: how the invitation looks — artwork, envelope, colours, logo and
 * timeline — beside a phone showing it as a guest will. A client works with
 * the event's own artwork, uploading it too; the office's library and the
 * timeline stay the office's.
 */
export function CampaignDesignStep({
  campaign,
  eventId,
  clientMode,
  onDirtyChange,
  onNext,
  event,
}: CampaignStepProps & { event?: CampaignEventInfo }) {
  const { alert, confirm } = useDialog();
  const { data: designs = [] } = useInviteDesigns({ eventId });
  const { data: campaigns } = useInviteCampaigns(eventId);
  const { data: timelines = [] } = useTimelines(eventId, { enabled: !clientMode });
  const createDesign = useCreateInviteDesign();
  const uploadPage = useUploadDesignPage();
  const removePage = useDeleteDesignPage();
  const { draft, set, dirty, save, saving } = useStepDraft(
    campaign,
    designOf,
    (d, c) => ({
      designId: d.designId || null,
      timelineId: d.timelineId || null,
      settings: withSettings(c, {
        showTimeline: d.showTimeline,
        backgroundColor: d.backgroundColor,
        accentColor: d.accentColor,
        includeEventLogo: d.includeEventLogo,
        showEnvelope: d.showEnvelope,
        envelopeSealLogo: d.envelopeSealLogo,
        envelopeColor: d.envelopeColor,
        sealColor: d.sealColor,
        envelopeMessage: d.envelopeMessage.trim() || undefined,
      }),
    }),
    onDirtyChange,
  );

  // A design an upload made but never filled (its first page failed) is not
  // artwork: it is left out of the picker, keeps the upload area showing, and
  // is filled by the next upload rather than joined by another empty one.
  const pickable = designs.filter((d) => hasArtwork(d) || d.id === draft.designId);
  // Only one no other campaign has chosen: a design whose last page was
  // removed may still be another campaign's invitation, and filling it would
  // change theirs. Until the campaigns are known, nothing is reused.
  const takenByOthers = new Set(
    (campaigns ?? []).filter((c) => c.id !== campaign.id).map((c) => c.designId),
  );
  const emptyDesign = campaigns
    ? designs.find((d) => !hasArtwork(d) && !takenByOthers.has(d.id))
    : undefined;
  const design = designs.find((d) => d.id === draft.designId);
  const pages = design?.pages ?? [];

  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      if (!isPdfFile(file) && !isArtworkImage(file)) {
        throw new Error('Artwork must be a PDF or a PNG, JPEG, WebP or GIF image.');
      }
      // A PDF becomes one image per page, so every browser and the email can show it.
      const uploads = isPdfFile(file) ? await pdfToImageFiles(file) : [file];
      if (uploads.length === 0) throw new Error('That PDF has no pages to import');
      let targetId = draft.designId || emptyDesign?.id || '';
      if (!targetId) {
        const created = await createDesign.mutateAsync({
          eventId,
          name: campaign.name || file.name,
        });
        targetId = created.id;
      }
      for (const [index, upload] of uploads.entries()) {
        try {
          await uploadPage.mutateAsync({ id: targetId, file: upload });
          // Claim a new design only once it holds artwork.
          if (index === 0 && targetId !== draft.designId) set('designId', targetId);
        } catch (err) {
          const reason = err instanceof Error ? err.message : 'Failed to upload artwork';
          throw new Error(
            index === 0
              ? reason
              : `${reason}. Only the first ${index} of ${uploads.length} pages were added — ` +
                  'remove them and try again.',
          );
        }
      }
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to upload artwork',
        variant: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePage = async (pageId: string) => {
    if (!draft.designId) return;
    const ok = await confirm({
      message: 'Remove this page from the invitation?',
      variant: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await removePage.mutateAsync({ designId: draft.designId, pageId });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to remove the page',
        variant: 'error',
      });
    }
  };

  const check = (key: keyof DesignDraft, label: string) => (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={draft[key] as boolean}
        onChange={(e) => set(key, e.target.checked as never)}
      />
      {label}
    </label>
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="space-y-5 min-w-0">
        <section>
          <h3 className="text-sm font-medium text-gray-700">Artwork</h3>
          {pickable.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <div className="min-w-56 flex-1">
                <SearchableListSelect
                  items={pickable}
                  value={draft.designId}
                  onChange={(value) => set('designId', typeof value === 'string' ? value : '')}
                  getValue={(d) => d.id}
                  getLabel={(d) => d.name}
                  getSubtitle={(d) =>
                    d.pages?.length
                      ? `${d.pages.length} page${d.pages.length === 1 ? '' : 's'}`
                      : null
                  }
                  placeholder="No artwork"
                  ariaLabel="Invitation design"
                  emptyMessage="No artwork uploaded yet"
                  clearable
                />
              </div>
              <ArtworkUploadButton
                label={pages.length > 0 ? 'Add page' : 'Upload'}
                busy={uploading}
                onFile={(file) => void handleUpload(file)}
              />
            </div>
          )}
          {pickable.length === 0 && (
            <ArtworkDropZone busy={uploading} onFile={(file) => void handleUpload(file)} />
          )}
          {pages.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-3">
              {pages.map((page, index) => (
                <div key={page.id} className="relative">
                  {page.artworkUrl && (
                    <img
                      src={page.artworkUrl}
                      alt={`Invitation page ${index + 1}`}
                      className="max-h-28 rounded-lg border border-gray-200"
                    />
                  )}
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                    Page {index + 1}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove invitation page ${index + 1}`}
                    onClick={() => void handleRemovePage(page.id)}
                    className="absolute right-1 top-1 flex min-h-11 min-w-11 items-center justify-center rounded bg-white/90 text-gray-500 hover:text-red-600 sm:min-h-0 sm:min-w-0 sm:p-1"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            An image, or a PDF — each of its pages becomes a page of the invitation. The first page
            goes in the email and the link preview; guests flip through the rest.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700">Envelope</h3>
          {check('showEnvelope', 'Open with an envelope')}
          {draft.showEnvelope && check('envelopeSealLogo', 'Seal it with the event logo')}
          <div className="flex flex-wrap items-end gap-4">
            {draft.showEnvelope && (
              <>
                <ColourField
                  id="campaign-envelope-color"
                  label="Envelope"
                  value={draft.envelopeColor}
                  onChange={(v) => set('envelopeColor', v)}
                />
                <ColourField
                  id="campaign-seal-color"
                  label="Seal"
                  value={draft.sealColor}
                  onChange={(v) => set('sealColor', v)}
                />
              </>
            )}
            <div className="min-w-56 flex-1">
              <label
                htmlFor="campaign-envelope-message"
                className="block text-xs font-medium text-gray-600"
              >
                {draft.showEnvelope ? 'Text on the letter' : 'Invitation heading'}
              </label>
              <input
                id="campaign-envelope-message"
                maxLength={200}
                value={draft.envelopeMessage}
                onChange={(e) => set('envelopeMessage', e.target.value)}
                placeholder="The event name"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm"
              />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700">Colours & logo</h3>
          <div className="flex flex-wrap items-end gap-4">
            <ColourField
              id="campaign-bg-color"
              label="Background"
              value={draft.backgroundColor}
              onChange={(v) => set('backgroundColor', v)}
            />
            <ColourField
              id="campaign-accent-color"
              label="Accent"
              value={draft.accentColor}
              onChange={(v) => set('accentColor', v)}
            />
            <button
              type="button"
              onClick={() => {
                set('backgroundColor', DEFAULT_BACKGROUND);
                set('accentColor', DEFAULT_INVITE_ACCENT);
                set('envelopeColor', DEFAULT_ENVELOPE_COLOR);
                set('sealColor', DEFAULT_INVITE_ACCENT);
              }}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              Reset colours
            </button>
          </div>
          {check('includeEventLogo', 'Show the event logo')}
        </section>

        {!clientMode && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Timeline</h3>
            {check('showTimeline', 'Show the event timeline')}
            {draft.showTimeline && (
              <SearchableListSelect
                items={timelines}
                value={draft.timelineId}
                onChange={(v) => set('timelineId', typeof v === 'string' ? v : '')}
                getValue={(tl) => tl.id}
                getLabel={(tl) => tl.title || 'Untitled timeline'}
                placeholder="Select a timeline…"
                ariaLabel="Timeline to display"
                clearable
              />
            )}
          </section>
        )}
      </div>

      <InvitePreview
        event={event}
        message={campaign.message}
        pages={pages.map((p) => p.artworkUrl).filter((url): url is string => !!url)}
        settings={{
          ...campaign.settings,
          backgroundColor: draft.backgroundColor,
          accentColor: draft.accentColor,
          includeEventLogo: draft.includeEventLogo,
          showEnvelope: draft.showEnvelope,
          envelopeSealLogo: draft.envelopeSealLogo,
          envelopeColor: draft.envelopeColor,
          sealColor: draft.sealColor,
          envelopeMessage: draft.envelopeMessage,
        }}
      />

      <div className="xl:col-span-2">
        <StepFooter
          dirty={dirty}
          saving={saving}
          onSave={() => void save()}
          onNext={
            onNext &&
            (async () => {
              if (dirty && !(await save())) return;
              onNext();
            })
          }
          nextLabel="Save & continue"
        />
      </div>
    </div>
  );
}
